import React, { useState } from 'react';
import { AlertCircle, CheckCircle2, Package, Send, X } from 'lucide-react';
import { toast } from 'sonner';

import { useCreateBatchProposalsMutation } from '@/apis/extended/proposalApi';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';

interface ClauseProposalData {
  clauseId: string;
  clauseHierarchicalId: string;
  clauseTitle: string;
  originalValue: any;
  proposedValue: any;
  message?: string;
}

interface ProposalSubmissionPanelProps {
  contractId: string;
  selectedProposals: Map<string, ClauseProposalData>;
  onClearAll: () => void;
  onRemoveProposal: (clauseId: string) => void;
  onSubmitSuccess?: () => void;
}

export const ProposalSubmissionPanel: React.FC<
  ProposalSubmissionPanelProps
> = ({
  contractId,
  selectedProposals,
  onClearAll,
  onRemoveProposal,
  onSubmitSuccess,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [createBatchProposals] = useCreateBatchProposalsMutation();

  const handleSubmit = async () => {
    if (selectedProposals.size === 0) {
      toast.error('No proposals selected');
      return;
    }

    setIsSubmitting(true);

    try {
      const proposalsArray = Array.from(selectedProposals.values()).map(
        (prop) => ({
          clauseId: prop.clauseId,
          clauseHierarchicalId: prop.clauseHierarchicalId,
          originalValue: prop.originalValue,
          proposedValue: prop.proposedValue,
          message: prop.message,
        })
      );

      const result = await createBatchProposals({
        contractId,
        proposals: proposalsArray as any,
      }).unwrap();

      toast.success(`Successfully submitted ${result.count} proposal(s)`, {
        description:
          result.errors && result.errors.length > 0
            ? `${result.errors.length} proposal(s) failed`
            : 'All proposals submitted successfully',
      });

      if (onSubmitSuccess) {
        onSubmitSuccess();
      }

      onClearAll();
    } catch (error) {
      console.error('Error submitting batch proposals:', error);
      toast.error('Failed to submit proposals', {
        description: 'Please try again or contact support',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatValue = (value: any): string => {
    if (value === null || value === undefined) return 'Not specified';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'number') return value.toLocaleString();
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  };

  if (selectedProposals.size === 0) {
    return null;
  }

  return (
    <div className="sticky bottom-0 left-0 right-0 bg-white border-t-2 border-blue-500 shadow-2xl z-50">
      <Card className="border-0 rounded-none">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Package className="h-5 w-5 text-blue-600" />
              <CardTitle className="text-lg">
                {selectedProposals.size} Proposal
                {selectedProposals.size !== 1 ? 's' : ''} Ready to Submit
              </CardTitle>
            </div>
            <div className="flex space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={onClearAll}
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                <X className="h-4 w-4 mr-2" />
                Clear All
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={isSubmitting}
                size="sm"
                className="bg-blue-600 hover:bg-blue-700"
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Submit All Proposals
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardHeader>
        <Separator />
        <CardContent className="pt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 max-h-60 overflow-y-auto">
            {Array.from(selectedProposals.entries()).map(([id, proposal]) => (
              <div
                key={id}
                className="p-3 bg-blue-50 border border-blue-200 rounded-lg relative group hover:bg-blue-100 transition-colors"
              >
                <button
                  onClick={() => onRemoveProposal(id)}
                  className="absolute top-2 right-2 text-gray-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X className="h-4 w-4" />
                </button>
                <h4 className="font-medium text-sm text-gray-900 mb-2 pr-6">
                  {proposal.clauseTitle}
                </h4>
                <div className="space-y-1 text-xs">
                  <div className="flex items-center space-x-2">
                    <span className="text-gray-500">From:</span>
                    <span className="text-gray-700 line-through">
                      {formatValue(proposal.originalValue)}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-gray-500">To:</span>
                    <span className="text-blue-600 font-medium">
                      {formatValue(proposal.proposedValue)}
                    </span>
                  </div>
                  {proposal.message && (
                    <div className="mt-2 text-gray-600 italic">
                      "{proposal.message}"
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {selectedProposals.size > 5 && (
            <div className="mt-3 text-center text-sm text-gray-500">
              <AlertCircle className="h-4 w-4 inline mr-1" />
              Scroll to see all selected proposals
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
